import { useMemo, useState } from "react";
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCenter, DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Type, Heading1, Image as ImgIcon, Youtube, Mail, Phone, Hash, User,
  Home as HomeIcon, TextCursorInput, AlignLeft, CircleDot, CheckSquare,
  ChevronDown, ToggleLeft, Sliders, Calendar, Clock, CalendarClock,
  ListChecks, Table2, ShieldCheck, PenLine, Star, Search, Calculator,
  FileUp, GripVertical, Trash2, Plus,
} from "lucide-react";

/**
 * LandingPageBuilder — drag-drop block canvas.
 *
 * Palette (matching the reference screenshot):
 *   Quick presets: Email, Phone, Number, Full name, Address
 *   Display Fields: Text display, Title display, Image, YouTube Video
 *   Input Fields:   Text short, Text long, Single/Multi choice,
 *                   Select dropdown, Switch, Range, Date, Time,
 *                   Date+Time, Input Table, Signature, Star Rating,
 *                   Lookup, Calculation, Inline Input, Terms & Cond.
 *   Advanced:       File upload
 *
 * State model:
 *   blocks: [{ id, type, props: { …type-specific… } }]
 * Blocks render top-to-bottom on the public detail page; each block
 * type has its own default props defined in DEFAULT_PROPS.
 */

// ─── Palette definition ───────────────────────────────────────
const PALETTE = [
  {
    group: "Quick presets",
    items: [
      { type: "email",      icon: Mail,       label: "Email" },
      { type: "phone",      icon: Phone,      label: "Phone" },
      { type: "number",     icon: Hash,       label: "Number" },
      { type: "full_name",  icon: User,       label: "Full name" },
      { type: "address",    icon: HomeIcon,   label: "Address" },
    ],
  },
  {
    group: "Display Fields",
    items: [
      { type: "text",       icon: Type,       label: "Text display" },
      { type: "title",      icon: Heading1,   label: "Title display" },
      { type: "image",      icon: ImgIcon,    label: "Image" },
      { type: "youtube",    icon: Youtube,    label: "Youtube Video" },
    ],
  },
  {
    group: "Input Fields",
    items: [
      { type: "text_short", icon: TextCursorInput, label: "Text input short" },
      { type: "text_long",  icon: AlignLeft,       label: "Text input long" },
      { type: "single",     icon: CircleDot,       label: "Single choice" },
      { type: "multi",      icon: CheckSquare,     label: "Multiple choice" },
      { type: "dropdown",   icon: ChevronDown,     label: "Select dropdown" },
      { type: "switch",     icon: ToggleLeft,      label: "Switch" },
      { type: "range",      icon: Sliders,         label: "Range" },
      { type: "date",       icon: Calendar,        label: "Date" },
      { type: "time",       icon: Clock,           label: "Time" },
      { type: "datetime",   icon: CalendarClock,   label: "Date and Time" },
      { type: "table",      icon: Table2,          label: "Input Table" },
      { type: "captcha",    icon: ShieldCheck,     label: "Google Captcha" },
      { type: "signature",  icon: PenLine,         label: "Signature" },
      { type: "rating",     icon: Star,            label: "Star Rating" },
      { type: "lookup",     icon: Search,          label: "Lookup" },
      { type: "calculation",icon: Calculator,      label: "Calculation" },
      { type: "inline",     icon: TextCursorInput, label: "Inline Input" },
      { type: "terms",      icon: ListChecks,      label: "Terms & Condition" },
    ],
  },
  {
    group: "Advanced",
    items: [
      { type: "file_upload", icon: FileUp,     label: "File upload" },
    ],
  },
];

const ICONS = Object.fromEntries(
  PALETTE.flatMap((g) => g.items.map((i) => [i.type, i.icon]))
);
const LABELS = Object.fromEntries(
  PALETTE.flatMap((g) => g.items.map((i) => [i.type, i.label]))
);

// Default props per block type. Rendered on public page + editable in settings.
const DEFAULT_PROPS = {
  email:       { label: "Work email",   required: true,  placeholder: "you@company.com" },
  phone:       { label: "Phone",        required: false, placeholder: "+1 555 000 0000" },
  number:      { label: "Number",       required: false, placeholder: "" },
  full_name:   { label: "Full name",    required: true,  placeholder: "Jane Doe" },
  address:     { label: "Address",      required: false, placeholder: "Street, City, ZIP" },
  text:        { html: "Paragraph text — click to edit." },
  title:       { text: "Section title", level: 2 },
  image:       { src: "", alt: "", width_pct: 100 },
  youtube:     { url: "" },
  text_short:  { label: "Short answer", required: false, placeholder: "" },
  text_long:   { label: "Long answer",  required: false, placeholder: "", rows: 4 },
  single:      { label: "Pick one",     required: false, options: ["Option A","Option B"] },
  multi:       { label: "Pick any",     required: false, options: ["Option 1","Option 2"] },
  dropdown:    { label: "Select…",      required: false, options: ["First","Second"] },
  switch:      { label: "Toggle",       default: false },
  range:       { label: "Range", min: 0, max: 100, step: 1, default: 50 },
  date:        { label: "Date",         required: false },
  time:        { label: "Time",         required: false },
  datetime:    { label: "Date and time",required: false },
  table:       { label: "Table",        columns: ["Col A","Col B"] },
  captcha:     { site_key: "" },
  signature:   { label: "Signature",    required: false },
  rating:      { label: "Rating",       max: 5, default: 0 },
  lookup:      { label: "Lookup",       placeholder: "" },
  calculation: { label: "Calculation",  formula: "" },
  inline:      { label: "Inline",       placeholder: "" },
  terms:       { label: "I agree to the Terms & Conditions", required: true },
  file_upload: { label: "Upload file",  required: false, accept: "" },
};

const uid = () => `blk_${Math.random().toString(36).slice(2, 10)}`;

export default function LandingPageBuilder({ blocks, onChange }) {
  const [selectedId, setSelectedId] = useState(null);
  const [dragType, setDragType] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const selected = useMemo(
    () => (blocks || []).find((b) => b.id === selectedId) || null,
    [blocks, selectedId]
  );

  const addBlock = (type) => {
    const nb = { id: uid(), type, props: { ...(DEFAULT_PROPS[type] || {}) } };
    onChange([...(blocks || []), nb]);
    setSelectedId(nb.id);
  };

  const removeBlock = (id) => {
    onChange((blocks || []).filter((b) => b.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const updateBlockProps = (id, patch) => {
    onChange((blocks || []).map((b) => (b.id === id ? { ...b, props: { ...b.props, ...patch } } : b)));
  };

  const handleDragEnd = (evt) => {
    setDragType(null);
    const { active, over } = evt;
    if (!over) return;

    // Palette drop → add
    if (String(active.id).startsWith("palette::")) {
      const type = String(active.id).replace("palette::", "");
      addBlock(type);
      return;
    }

    // Reorder canvas
    if (active.id !== over.id) {
      const oldIdx = blocks.findIndex((b) => b.id === active.id);
      const newIdx = blocks.findIndex((b) => b.id === over.id);
      if (oldIdx >= 0 && newIdx >= 0) onChange(arrayMove(blocks, oldIdx, newIdx));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={(e) => setDragType(String(e.active.id).replace("palette::", ""))}
      onDragEnd={handleDragEnd}
      data-testid="lp-builder"
    >
      <div className="grid grid-cols-12 gap-3 h-full min-h-0">
        {/* Palette */}
        <aside className="col-span-4 overflow-y-auto border rounded bg-white p-3 max-h-full" data-testid="lp-palette">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Add Element</div>
          <p className="text-xs text-slate-500 mb-3">Click to add, or drag onto the canvas.</p>
          {PALETTE.map((g) => (
            <div key={g.group} className="mb-4">
              <div className="text-xs font-bold text-slate-800 mb-1.5">{g.group}</div>
              <div className="grid grid-cols-2 gap-1.5">
                {g.items.map(({ type, icon: Icon, label }) => (
                  <PaletteChip key={type} type={type} Icon={Icon} label={label} onClick={() => addBlock(type)}/>
                ))}
              </div>
            </div>
          ))}
        </aside>

        {/* Canvas */}
        <main className="col-span-5 overflow-y-auto border rounded bg-slate-100 p-4" data-testid="lp-canvas">
          {(!blocks || blocks.length === 0) ? (
            <div className="rounded border border-dashed border-slate-300 p-10 text-center text-slate-400 text-sm bg-white">
              Empty canvas. Click any element in the palette to add it here.
            </div>
          ) : (
            <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {blocks.map((b) => (
                  <CanvasBlock
                    key={b.id} block={b}
                    active={selectedId === b.id}
                    onSelect={() => setSelectedId(b.id)}
                    onRemove={() => removeBlock(b.id)}
                  />
                ))}
              </div>
            </SortableContext>
          )}
        </main>

        {/* Settings */}
        <aside className="col-span-3 overflow-y-auto border rounded bg-white p-3" data-testid="lp-settings">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Settings</div>
          {!selected ? (
            <p className="text-xs text-slate-500">Select a block to edit its properties.</p>
          ) : (
            <SettingsPanel block={selected} onUpdate={(patch) => updateBlockProps(selected.id, patch)}/>
          )}
        </aside>
      </div>

      <DragOverlay>
        {dragType && LABELS[dragType] ? (
          <div className="rounded bg-cyan-600 text-white px-3 py-1.5 text-xs font-semibold shadow-lg">
            + {LABELS[dragType]}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function PaletteChip({ type, Icon, label, onClick }) {
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id: `palette::${type}` });
  return (
    <button
      ref={setNodeRef} {...attributes} {...listeners}
      onClick={onClick} type="button"
      data-testid={`palette-${type}`}
      className={`flex items-center gap-2 rounded border border-dashed border-slate-300 bg-white px-2.5 py-2 text-xs font-medium text-slate-700 hover:border-cyan-500 hover:bg-cyan-50 hover:text-cyan-700 transition ${isDragging ? "opacity-40" : ""}`}
    >
      <Icon className="w-3.5 h-3.5 shrink-0"/>
      <span className="truncate">{label}</span>
    </button>
  );
}

function CanvasBlock({ block, active, onSelect, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const Icon = ICONS[block.type] || Type;
  return (
    <div
      ref={setNodeRef} style={style}
      className={`rounded border bg-white p-3 flex items-start gap-3 ${active ? "border-cyan-500 ring-2 ring-cyan-200" : "border-slate-200"} ${isDragging ? "opacity-60" : ""}`}
      onClick={onSelect}
      data-testid={`canvas-block-${block.type}`}
    >
      <button type="button" {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-slate-400" onClick={(e) => e.stopPropagation()} title="Drag to reorder">
        <GripVertical className="w-4 h-4"/>
      </button>
      <Icon className="w-4 h-4 mt-0.5 shrink-0 text-cyan-600"/>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-bold text-slate-800">{LABELS[block.type] || block.type}</div>
        <BlockPreview block={block}/>
      </div>
      <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }} className="p-1 text-slate-400 hover:text-rose-600" title="Remove">
        <Trash2 className="w-4 h-4"/>
      </button>
    </div>
  );
}

function BlockPreview({ block }) {
  const p = block.props || {};
  switch (block.type) {
    case "text":  return <div className="text-[13px] text-slate-600 line-clamp-2 mt-0.5">{p.html || "—"}</div>;
    case "title": return <div className="text-[13px] font-bold text-slate-800 mt-0.5">{p.text || "—"}</div>;
    case "image": return p.src ? <img src={p.src} alt="" className="mt-1 max-h-16 rounded border"/> : <div className="text-[11px] text-slate-400 mt-0.5">no image URL</div>;
    case "youtube": return <div className="text-[11px] text-slate-500 mt-0.5 truncate">{p.url || "no URL"}</div>;
    case "single":
    case "multi":
    case "dropdown":
      return <div className="text-[11px] text-slate-500 mt-0.5">{p.label} · {(p.options || []).length} options</div>;
    default:
      return <div className="text-[11px] text-slate-500 mt-0.5">{p.label || p.placeholder || "—"}{p.required ? " · required" : ""}</div>;
  }
}

// ─── Settings panel for the selected block ────────────────────
const SettingsRow = ({ label, children }) => (
  <div className="mb-2.5">
    <Label className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5 block">{label}</Label>
    {children}
  </div>
);

function SettingsPanel({ block, onUpdate }) {
  const p = block.props || {};
  const Row = SettingsRow;
  const setOpts = (v) => onUpdate({ options: v.split("\n").map((s) => s.trim()).filter(Boolean) });
  const setCols = (v) => onUpdate({ columns: v.split("\n").map((s) => s.trim()).filter(Boolean) });

  return (
    <div className="text-sm" data-testid={`settings-${block.type}`}>
      <div className="text-xs text-slate-500 mb-3">Type: <code className="text-[11px] bg-slate-100 rounded px-1">{block.type}</code></div>

      {/* Common: label */}
      {"label" in p && (
        <Row label="Label">
          <Input value={p.label || ""} onChange={(e) => onUpdate({ label: e.target.value })} className="h-8 text-sm" data-testid="setting-label"/>
        </Row>
      )}

      {/* Text / paragraph */}
      {block.type === "text" && (
        <Row label="Content (HTML supported)">
          <Textarea rows={4} value={p.html || ""} onChange={(e) => onUpdate({ html: e.target.value })} className="text-sm" data-testid="setting-html"/>
        </Row>
      )}

      {/* Title */}
      {block.type === "title" && (
        <>
          <Row label="Text">
            <Input value={p.text || ""} onChange={(e) => onUpdate({ text: e.target.value })} className="h-8 text-sm" data-testid="setting-text"/>
          </Row>
          <Row label="Level (1–4)">
            <Input type="number" min="1" max="4" value={p.level || 2} onChange={(e) => onUpdate({ level: Math.max(1, Math.min(4, parseInt(e.target.value) || 2)) })} className="h-8 text-sm" data-testid="setting-level"/>
          </Row>
        </>
      )}

      {/* Image */}
      {block.type === "image" && (
        <>
          <Row label="Image URL"><Input value={p.src || ""} onChange={(e) => onUpdate({ src: e.target.value })} className="h-8 text-sm" data-testid="setting-src"/></Row>
          <Row label="Alt text"><Input value={p.alt || ""} onChange={(e) => onUpdate({ alt: e.target.value })} className="h-8 text-sm"/></Row>
          <Row label="Width (%)"><Input type="number" min="10" max="100" value={p.width_pct || 100} onChange={(e) => onUpdate({ width_pct: parseInt(e.target.value) || 100 })} className="h-8 text-sm"/></Row>
        </>
      )}

      {/* YouTube */}
      {block.type === "youtube" && (
        <Row label="YouTube URL"><Input value={p.url || ""} onChange={(e) => onUpdate({ url: e.target.value })} className="h-8 text-sm" data-testid="setting-url"/></Row>
      )}

      {/* Placeholder */}
      {"placeholder" in p && (
        <Row label="Placeholder"><Input value={p.placeholder || ""} onChange={(e) => onUpdate({ placeholder: e.target.value })} className="h-8 text-sm"/></Row>
      )}

      {/* Required */}
      {"required" in p && (
        <div className="flex items-center gap-2 mb-3 mt-1">
          <Switch checked={!!p.required} onCheckedChange={(v) => onUpdate({ required: !!v })} data-testid="setting-required"/>
          <span className="text-xs">Required</span>
        </div>
      )}

      {/* Options (single/multi/dropdown) */}
      {("options" in p) && (
        <Row label="Options (one per line)">
          <Textarea rows={4} value={(p.options || []).join("\n")} onChange={(e) => setOpts(e.target.value)} className="text-sm" data-testid="setting-options"/>
        </Row>
      )}

      {/* Range specifics */}
      {block.type === "range" && (
        <div className="grid grid-cols-3 gap-2">
          <Row label="Min"><Input type="number" value={p.min ?? 0} onChange={(e) => onUpdate({ min: parseInt(e.target.value) })} className="h-8 text-sm"/></Row>
          <Row label="Max"><Input type="number" value={p.max ?? 100} onChange={(e) => onUpdate({ max: parseInt(e.target.value) })} className="h-8 text-sm"/></Row>
          <Row label="Step"><Input type="number" value={p.step ?? 1} onChange={(e) => onUpdate({ step: parseInt(e.target.value) })} className="h-8 text-sm"/></Row>
        </div>
      )}

      {/* Rating specifics */}
      {block.type === "rating" && (
        <Row label="Max stars"><Input type="number" min="1" max="10" value={p.max ?? 5} onChange={(e) => onUpdate({ max: parseInt(e.target.value) })} className="h-8 text-sm"/></Row>
      )}

      {/* Table columns */}
      {block.type === "table" && (
        <Row label="Columns (one per line)">
          <Textarea rows={3} value={(p.columns || []).join("\n")} onChange={(e) => setCols(e.target.value)} className="text-sm"/>
        </Row>
      )}

      {/* File upload */}
      {block.type === "file_upload" && (
        <Row label="Accept (MIME)"><Input value={p.accept || ""} onChange={(e) => onUpdate({ accept: e.target.value })} placeholder="e.g. application/pdf,image/*" className="h-8 text-sm"/></Row>
      )}

      {/* Calculation formula */}
      {block.type === "calculation" && (
        <Row label="Formula"><Input value={p.formula || ""} onChange={(e) => onUpdate({ formula: e.target.value })} className="h-8 text-sm" placeholder="e.g. field_a + field_b"/></Row>
      )}
    </div>
  );
}
